import type { GeoPoint } from '@metro/location'

/**
 * Station shape the metro location-consumer service reasons about.
 * Deliberately minimal — concrete stations are mapped into this shape
 * by an injected provider (spec §2b, domain logic that lives host-side,
 * never inside the product-agnostic `Location` module).
 */
export interface GeoStation {
  readonly id: string
  readonly name: string
  readonly point: GeoPoint
}

/**
 * Injected seam: resolves the N nearest stations to a coordinate.
 * The route feature uses `limit: 1`; future Suggester V2 uses `limit > 1`.
 */
export interface NearestStationProvider {
  nearestStations(to: GeoPoint, limit: number): Promise<GeoStation[]>
}

/**
 * Great-circle distance in meters between two coordinates (haversine).
 */
export function haversineMeters(a: GeoPoint, b: GeoPoint): number {
  const R = 6_371_000
  const dLat = ((b.latitude - a.latitude) * Math.PI) / 180
  const dLon = ((b.longitude - a.longitude) * Math.PI) / 180
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a.latitude * Math.PI) / 180) *
      Math.cos((b.latitude * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2
  return R * 2 * Math.asin(Math.sqrt(h))
}

/**
 * In-memory provider over a fixed station list — used for module tests
 * and as a reference implementation of the ordering contract.
 */
export class StubNearestStationProvider implements NearestStationProvider {
  private readonly stations: GeoStation[]

  constructor(stations: GeoStation[]) {
    this.stations = stations
  }

  async nearestStations(to: GeoPoint, limit: number): Promise<GeoStation[]> {
    return [...this.stations]
      .sort((x, y) => haversineMeters(x.point, to) - haversineMeters(y.point, to))
      .slice(0, limit)
  }
}
